import { useBreakpoint } from '@lib/hooks'
import { breakpointGTE } from '@lib/hooks/use-breakpoint'
import CopyButton from '@modules/bh/quotes/CopyButton'
import QuoteStatusRenderer from '@modules/bh/quotes/QuoteStatusRenderer'
import { humanizer } from '@modules/bh/quotes/helpers'
import {
  CellClickedEvent,
  ColDef,
  ColumnApi,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  RowClassParams,
  ValueFormatterParams,
} from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import 'react-toastify/dist/ReactToastify.css'
import { useWindowSize } from 'usehooks-ts'
import DateFilter from './DateFilter'
import MyQuotesGridDataSource from './MyQuotesGridDataSource'
import ProductTypeFilter from './ProductTypeFilter'
import QuoteStatusFilter from './QuoteStatusFilter'
import TextFilter from './TextFilter'

interface MyQuotesGridProps {
  clearFiltersRef: React.MutableRefObject<
    Maybe<{
      clearFilters: () => void
    }>
  >
  onFirstDataRendered: (event: FirstDataRenderedEvent) => void
  onCellClicked?: (event: CellClickedEvent, quote: Maybe<Quote>) => void
  dataSource: MyQuotesGridDataSource
  pageSize: number
  userDiscountPercentage: number
}

function MyQuotesGrid({
  clearFiltersRef,
  onFirstDataRendered,
  onCellClicked,
  dataSource,
  pageSize,
  userDiscountPercentage,
}: MyQuotesGridProps) {
  const gridApiRef = useRef<Maybe<GridApi>>(null)
  const gridColumnApiRef = useRef<Maybe<ColumnApi>>(null)

  const breakpoint = useBreakpoint()

  function getRowStyle(params: RowClassParams) {
    return {
      backgroundColor:
        parseInt(params.node.id!) % 2 === 0 ? 'white' : '#CA342C11',
    }
  }

  function handleFirstDataRendered(event: FirstDataRenderedEvent) {
    // event.columnApi.autoSizeAllColumns()
    event.api.sizeColumnsToFit()
    onFirstDataRendered(event)
  }

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
    }),
    []
  )

  const colDefs: ColDef[] = useMemo(() => {
    return getColDefsForBreakpointSize(
      breakpoint ?? 'xs',
      userDiscountPercentage
    )
  }, [breakpoint])

  function onGridReady(e: GridReadyEvent) {
    gridApiRef.current = e.api
    gridColumnApiRef.current = e.columnApi
  }

  function handleCellClicked(e: CellClickedEvent) {
    const quote = dataSource.getQuote(e.data.viewableId)
    if (onCellClicked) {
      onCellClicked(e, quote)
    }
  }

  useImperativeHandle(clearFiltersRef, () => {
    return {
      clearFilters: () => {
        if (gridApiRef.current) {
          gridApiRef.current.setFilterModel(null)
          gridApiRef.current.onFilterChanged()
        }
      },
    }
  })

  const windowSize = useWindowSize()

  // Throttle windowSize change and autoSize all columns on throttle
  useEffect(() => {
    const throttle = setTimeout(() => {
      if (gridColumnApiRef.current) {
        // gridColumnApiRef.current.autoSizeAllColumns()
        gridApiRef.current?.sizeColumnsToFit()
      }
    }, 100)

    return () => {
      clearTimeout(throttle)
    }
  }, [windowSize.width])

  return (
    <div className="ag-theme-material min-h-[500px] flex flex-1 flex-grow">
      <div style={{ minHeight: 500, width: '100%' }}>
        <AgGridReact
          suppressMenuHide
          domLayout="autoHeight"
          overlayNoRowsTemplate="No Data"
          defaultColDef={defaultColDef}
          columnDefs={colDefs}
          suppressMovableColumns
          suppressDragLeaveHidesColumns
          animateRows
          pagination
          // onFilterChanged={onFilterChanged}
          paginationPageSize={15}
          onCellClicked={handleCellClicked}
          headerHeight={40}
          rowHeight={40}
          rowBuffer={10}
          cacheBlockSize={pageSize}
          maxConcurrentDatasourceRequests={2}
          infiniteInitialRowCount={0}
          getRowStyle={getRowStyle}
          onFirstDataRendered={handleFirstDataRendered}
          datasource={dataSource}
          rowModelType="infinite"
          onGridReady={onGridReady}
        />
      </div>
    </div>
  )
}

export default MyQuotesGrid
// export default React.memo(MyQuotesGrid, (prevProps, nextProps) => {
//   if (prevProps.searchText !== nextProps.searchText) {
//     return false
//   }
//   return true
// })

function getDisplayTextForQuoteStatus(quote: Quote): string {
  const expired = new Date(quote.expiry).getTime() < Date.now()
  const expedite = quote.expedite
  const quoteStatus = quote.quoteStatus.description

  let displayText = quoteStatus as string

  if (expired) {
    if (quoteStatus === 'Ordered') {
      displayText = 'Ordered'
    } else {
      displayText = 'Expired'
    }
  } else {
    switch (quoteStatus) {
      case 'Saved':
        break

      case 'Pending':
        if (expedite) {
          displayText = 'Pending Expedite'
        } else {
          displayText = 'Waiting for Shipping Quote'
        }
        break

      case 'Available':
        // If call is true and status is available, then user submitted a call request,
        // and Barnhill approved on admin side, so mark as 'Call Completed'.
        if (quote.call === true) {
          displayText = 'Call Completed'
        } else if (quote.shippingQuoteRequested === true) {
          displayText = 'Shipping Quoted'
        }
        break

      case 'Ordered':
        break

      case 'Expired':
        break

      default:
        break
    }
  }

  return displayText
}

function getCurrentTotal(
  subtotal: string,
  userDiscountPercentage: number,
  shippingFee: number,
  expediteFee: number
): string {
  subtotal = (Math.round(parseFloat(subtotal) * 100) / 100).toFixed(2)
  var discount = (userDiscountPercentage / 100) * parseFloat(subtotal)
  subtotal = (parseFloat(subtotal) - discount).toFixed(2)
  var total = (parseFloat(subtotal) + shippingFee + expediteFee).toFixed(2)

  return total
}

function getColDefsForBreakpointSize(
  breakpoint: Breakpoint,
  userDiscountPercentage: number
): ColDef[] {
  const colDefs: ColDef[] = [
    {
      // Note this field name is also bound to some CellClickedEvent logic below
      field: 'viewableId',
      headerName: 'Quote ID',
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) => {
        const quote = params.data
        if (!quote) {
          return <></>
        }
        return (
          <CopyButton
            copyText={quote.viewableId}
            copyTextClass="right-5 top-0"
            displayText={quote.viewableId}
            hoverText="Copy ID"
            hoverTextClass="left-4/5 top-4"
          />
        )
      },
      onCellClicked: function (params: CellClickedEvent) {
        const viewableId = params.data.viewableId
        navigator.clipboard.writeText(viewableId)
      },
      filter: TextFilter,
    },
    {
      field: 'customerPO',
      headerName: 'PO #',
      filter: TextFilter,
    },
    {
      field: 'productType',
      headerName: 'Product Type',
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return humanizer(params.value)
        }

        return ''
      },
      filter: ProductTypeFilter,
    },
  ]

  if (breakpointGTE(breakpoint, 'sm')) {
    colDefs.push(
      ...[
        {
          field: 'quoteStatus',
          headerName: 'Status',
          cellRenderer: (params: ICellRendererParams) => {
            if (params.data?.quoteStatus === undefined) {
              return <></>
            }

            const displayText = getDisplayTextForQuoteStatus(params.data)
            return <QuoteStatusRenderer displayText={displayText} />
          },
          comparator: (
            valueA: any,
            valueB: any,
            nodeA: IRowNode<Quote>,
            nodeB: IRowNode<Quote>
          ) => {
            if (!nodeA.data?.quoteStatus || !nodeB.data?.quoteStatus) return 0

            const a = getDisplayTextForQuoteStatus(nodeA.data)
            const b = getDisplayTextForQuoteStatus(nodeB.data)
            return a.localeCompare(b)
          },
          filter: QuoteStatusFilter,
          // filter: 'agTextColumnFilter',
          // filterParams: {
          //   filterOptions: ['contains'],
          //   maxNumConditions: 1,
          // },
        },
        {
          field: 'subTotal',
          headerName: 'Total',
          valueFormatter: (params: ValueFormatterParams) => {
            const quote = params.data

            if (!quote) {
              return ''
            } else if (quote.quoteStatus.description === 'Call') {
              return 'TBD'
            }

            return params.value
              ? `$${getCurrentTotal(
                  params.value,
                  userDiscountPercentage,
                  quote.shippingFee,
                  quote.expediteFee
                )}`
              : ''
          },
        },
      ]
    )
  }

  if (breakpointGTE(breakpoint, 'md')) {
    colDefs.push(
      ...[
        // {
        //   field: 'shippingFee',
        //   headerName: 'Shipping Cost',
        //   valueFormatter: (params: ValueFormatterParams) => {
        //     return !!params.value ? `$${params.value.toFixed(2)}` : ''
        //   },
        // },
        {
          field: 'expiry',
          headerName: 'Expires',
          valueFormatter: DateFormatter,
        },
        {
          field: 'created',
          headerName: 'Created',
          valueFormatter: DateFormatter,
          filter: DateFilter,
        },
      ]
    )
  }

  return colDefs
}

function DateFormatter(params: ValueFormatterParams) {
  const date = new Date(params.value)
  // Return date with MM/DD/YYYY format
  const formattedDate = date.toLocaleDateString()
  if (formattedDate === 'Invalid Date') {
    return ''
  }
  return formattedDate
}
